import { useMemo, useSyncExternalStore } from "react";
import { balanceStore, type BalanceSnapshot, type ConnectOptions } from "./balanceStore";
import { serialSupport, type SerialSupport } from "./serialTransport";
import type { SimulatedBalance } from "./simulator";
import type { TransportKind } from "./types";

export interface UseBalance extends BalanceSnapshot {
  /** Opens a connection: "serial" shows the browser's port picker (call it from a click). */
  connect: (kind: TransportKind, options?: ConnectOptions) => Promise<void>;
  disconnect: () => Promise<void>;
  /** Sends the protocol's tare command (no-op for keyboard mode). */
  tare: () => Promise<void>;
  zero: () => Promise<void>;
  /** Asks for model and serial number again (stored on batches). */
  identify: () => Promise<void>;
  requestStable: () => Promise<void>;
  /** Reopens the remembered USB balance without the picker. */
  reconnectRemembered: () => Promise<boolean>;
  /** The simulated balance while the simulator is connected. */
  simulator: SimulatedBalance | null;
  serial: SerialSupport;
  isConnected: boolean;
}

/**
 * The shared balance connection: live reading, combined stability flag, identity and errors, plus the
 * actions. Every component calling it sees the same connection.
 */
export function useBalance(): UseBalance {
  const snapshot = useSyncExternalStore(balanceStore.subscribe, balanceStore.getSnapshot, balanceStore.getSnapshot);
  return useMemo(
    () => ({
      ...snapshot,
      connect: balanceStore.connect,
      disconnect: balanceStore.disconnect,
      tare: balanceStore.tare,
      zero: balanceStore.zero,
      identify: balanceStore.identify,
      requestStable: balanceStore.requestStable,
      reconnectRemembered: balanceStore.reconnectRemembered,
      simulator: balanceStore.simulator,
      serial: serialSupport(),
      isConnected: snapshot.status === "connected",
    }),
    [snapshot],
  );
}
